/**
 * Events data-access adapter — the client-wrapper boundary.
 *
 * This is the ONLY place the contract DTO (`StudioEventDto` from
 * @v0y4ge/api-contracts) is reconciled to the shape the verbatim Studio
 * Events UI consumes. The UI stays byte-for-byte as authored; everything
 * that differs between "prototype seed shape" and "contract shape" is mapped
 * here. When the screen deploys, the SAME api-client calls hit real infra and
 * this adapter is unchanged — only the transport underneath differs.
 *
 * Source of truth for the field map: wiring/events-list-reconciliation.md.
 */

import type { StudioEventDto, StudioEventStatus } from "@v0y4ge/api-contracts";
import { friendlyDate, friendlyTimeRange, tzNameLabel } from "./datetime";

/** The row shape the Studio Events list renders (mirrors the prototype seed). */
export interface UIEvent {
  id: string;
  title: string;
  /** Subtitle under the title — the event's category (prototype showed a template name). */
  template: string;
  /** Event description — carried onto the UI row (docket 2026-07-13 §1a) so the
   * local cache backfills the wizard's Essentials step without a refetch. */
  description: string;
  /** "Fri 8 May 2026" — derived in the event's own timezone. */
  date: string;
  /** "10:00 — 12:00" — derived in the event's own timezone (24h). */
  time: string;
  /** Short name of the event's own zone on its start date ("CDT"), shown after
   *  `time` so a reader knows which zone it is in. Empty when the event has no zone. */
  zone?: string;
  location: string;
  host: string;
  /** Canonical host: the `staff.id` behind `host` (contract `StudioEventDto.staffId`),
   * or null for a free-text / legacy label or no host at all. `host` stays the
   * display label; identity travels here and is never derived from the name. */
  hostId?: string | null;
  registered: number;
  /** The confirmed roster: registered + checked in, pending and waitlist excluded (contract:
   * confirmed_registration_count). Older servers omit it, so it falls back to registration_count (CC, 2026-10-05). */
  confirmed?: number;
  /** Whether cancelling now sends guests a message (contract cancellationNotice, CC 0340). Absent = unknown. */
  cancellationNotice?: { sends: boolean; reason: "switched_off" | "not_configured" | null };
  /** Checked-in count (contract: checked_in_count). */
  checked: number;
  /** Current waitlist size (contract: waitlist_count). */
  waitlist: number;
  /** Raw capacity; 0 ⇒ unlimited (see capacityLabel). */
  capacity: number;
  /** Prototype status vocabulary. */
  status: UIEventStatus;
  /** Deterministic accent color derived client-side from id (presentational). */
  hue: string;
  /** 16:9 hero image, if any (contract: imageUrl — camelCase-canonical, #734). */
  heroImage: string | null;
  format: string;
  approvalStatus: string;
  tags: string[];
  /** Booking questions/groups ASSIGNED to this event (registration form), in
   * order. `{kind:'question'|'group', id}` — resolve against the question
   * library. Empty ⇒ none assigned. (Mirrors creation cfg.bookingQuestions.) */
  bookingQuestions?: { kind: "question" | "group"; id: string }[];
  /** Survey (form) ids ASSIGNED to this event. Empty ⇒ none assigned. */
  surveys?: string[];
  /** Ticket-type ids ASSIGNED to this event — resolve against the ticket-type
   * library. Empty ⇒ none assigned. (Mirrors creation cfg.ticketTypeIds.) */
  ticketTypeIds?: string[];
  /** Payments (2026-08-04) — paid-vs-free + revenue summary for the events list
   * Paid/Free pill and Revenue column. All OPTIONAL and only populated once the
   * contract DTO carries a `payment` block; absent ⇒ the row renders as Free. */
  isPaid?: boolean;
  priceFromMinor?: number;
  revenueMinor?: number;
  paidRegistrations?: number;
  currency?: string;
  /** The untouched contract row, for any deeper need (detail nav, etc.). */
  raw: StudioEventDto;
  /** True once the event has been published at least once (set on take-offline).
   * Distinguishes a never-published draft from a previously-live one that was
   * taken offline to edit. Interim client-only flag — see changelog. */
  wasPublished?: boolean;
}

export type UIEventStatus = "live" | "upcoming" | "draft" | "canceled" | "past";

/* ── Status enum reconcile (contract → prototype vocab) ───────────────────
 * contract: live | scheduled | completed | cancelled | archived
 * prototype: live | upcoming | draft | canceled | past
 * Confirmed with Claude Code: past = completed + archived; cancelled stays its
 * own bucket. The contract has NO `draft` status by design — a draft is simply
 * a not-yet-published event. The publish transition sets status→live AND
 * visibility→public, so an unpublished (scheduled) event has visibility!=='public'
 * (i.e. 'hidden'). We therefore treat a scheduled-but-unpublished event as draft;
 * terminal states (past/canceled) are never draft regardless of visibility.
 * (Claude Code may add an explicit `published`/`is_draft` flag to the list DTO —
 * when present, prefer reading it directly over this derivation.) */
const STATUS_MAP: Record<StudioEventStatus | "draft", UIEventStatus> = {
  live: "live",
  scheduled: "upcoming",
  completed: "past",
  cancelled: "canceled",
  archived: "past",
  // 2026-07-12 docket §4 — declared AHEAD of the contract adding 'draft' to
  // StudioEventStatusSchema (the one CD-gated item). The widened key type
  // (`StudioEventStatus | "draft"`) keeps this exhaustive Record compiling both
  // BEFORE the enum lands (extra known key, no TS2353) and AFTER (required key
  // present, no TS2741 — the exact mechanism behind the #691 revert). Once the
  // enum ships, `| "draft"` collapses into StudioEventStatus and can be dropped.
  draft: "draft",
};

export function mapStatus(dto: StudioEventDto): UIEventStatus {
  // Once the contract 'draft' status ships, a draft row maps directly via
  // STATUS_MAP; the visibility-derivation below remains for pre-enum rows.
  const ui = STATUS_MAP[dto.status] ?? "upcoming";
  if (ui === "upcoming" && dto.visibility !== "public") {
    // An invitation-only / approval-gated PUBLISHED event is `scheduled` +
    // non-public by design — it is NOT a draft. Distinguish it from a genuinely
    // unpublished event via an explicit flag the DTO carries (`invitationOnly`
    // / `published`). Only fall back to "draft" when neither flag is set.
    const d = dto as unknown as { invitationOnly?: boolean; published?: boolean };
    if (d.invitationOnly === true || d.published === true) return ui;
    return "draft";
  }
  return ui;
}

/* ── Hue: deterministic accent from id (presentational, client-derived) ──── */
const HUES = [
  "#5142C7", "#2D60EB", "#027D47", "#C2410C", "#B4309E", "#0E7490", "#B91C1C", "#7C3AED",
];
export function deriveHue(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return HUES[hash % HUES.length]!;
}

/* ── Payments summary reconcile (contract payment block → UI fields) ────────
 * Optional + defensive: the events list DTO only carries `payment` once the
 * contract models it, so this reads through a cast and returns {} when absent.
 * Purely additive — an event with no payment block renders exactly as before. */
function derivePayment(dto: StudioEventDto): Pick<UIEvent, "isPaid" | "priceFromMinor" | "revenueMinor" | "paidRegistrations" | "currency"> {
  const p = (dto as unknown as {
    payment?: { is_paid?: boolean; price_from_minor?: number; revenue_minor?: number; paid_registrations?: number; currency?: string };
  }).payment;
  if (!p) return {};
  return {
    isPaid: p.is_paid,
    priceFromMinor: p.price_from_minor,
    revenueMinor: p.revenue_minor,
    paidRegistrations: p.paid_registrations,
    currency: p.currency,
  };
}

/* ── Date / time display, rendered in the event's own timezone ──────────────
 * All user-facing formatting goes through lib/datetime (locale + timezone
 * aware), so the clock (12h/24h) and date ordering follow the configured
 * locale and each event renders in its OWN venue timezone. */
function fmtDate(iso: string, tz: string): string {
  return friendlyDate(iso, tz || undefined);
}
export function fmtTimeRange(startIso: string, endIso: string, tz: string): string {
  return friendlyTimeRange(startIso, endIso || undefined, tz || undefined);
}

/** capacity 0 ⇒ "Unlimited" — never render a bare 0 (contract convention). */
export function capacityLabel(capacity: number): string {
  return capacity > 0 ? capacity.toLocaleString() : "Unlimited";
}

/** #749 residual-2 (CD-BOUNCE-drop19): derive a display label from the structured
 * EventLocation.address when an event carries a geocoded location but no separate
 * name — so the detail Overview never shows "—" for an event saved with only an
 * address / structured location. */
function structuredLocationLabel(loc: StudioEventDto["location"]): string {
  if (!loc || !loc.address) return "";
  const a = loc.address;
  return [a.street, a.city, a.region].filter(Boolean).join(", ");
}

export function toUIEvent(dto: StudioEventDto): UIEvent {
  const tz = dto.timezone;
  return {
    id: dto.id,
    title: dto.title,
    template: dto.category ?? "",
    description: dto.description ?? "",
    date: fmtDate(dto.startsAt, tz),
    time: fmtTimeRange(dto.startsAt, dto.endsAt, tz),
    zone: tzNameLabel(tz, dto.startsAt),
    // #749 residual-2: fall through to the persisted address string / structured
    // location before giving up on "—" (Overview read `locationName||location_name`
    // only, so an event saved with just an address showed blank).
    location: dto.locationName || dto.location_name || dto.locationAddress || dto.location_address || structuredLocationLabel(dto.location) || (dto.format === "virtual" ? "Online" : "—"),
    host: dto.staffName || "—",
    hostId: dto.staffId ?? null,
    /* No assignedStaff (JAI-2328): people on an event are event duty shifts (listEventDutyShifts). */
    registered: dto.registration_count,
    confirmed: (dto as { confirmed_registration_count?: number }).confirmed_registration_count ?? dto.registration_count,
    ...(dto.cancellationNotice ? { cancellationNotice: dto.cancellationNotice } : {}),
    checked: dto.checked_in_count,
    waitlist: dto.waitlist_count,
    capacity: dto.capacity,
    status: mapStatus(dto),
    hue: deriveHue(dto.id),
    heroImage: dto.imageUrl ?? null,
    format: dto.format,
    approvalStatus: dto.approval_status,
    tags: dto.tags ?? [],
    bookingQuestions: (dto as unknown as { bookingQuestions?: { kind: "question" | "group"; id: string }[] }).bookingQuestions ?? [],
    surveys: (dto as unknown as { surveys?: string[] }).surveys ?? [],
    ticketTypeIds: (dto as unknown as { ticketTypeIds?: string[] }).ticketTypeIds ?? [],
    ...derivePayment(dto),
    raw: dto,
  };
}

/* ── Build a UI row from the creation wizard config (interim client create) ──
 * The wizard config (CreationStore) is prototype-shaped; this maps it to a
 * UIEvent so a just-created/edited event shows on the list immediately. At
 * wiring time the wizard calls createStudioEvent/updateStudioEvent and the list
 * refetches — this helper is the interim stand-in for that round-trip. */
export function uiEventFromCreation(cfg: Record<string, unknown>, opts: { id?: string; status: UIEventStatus }): UIEvent {
  const id = opts.id || `ev_${Date.now().toString(36)}`;
  const method = (cfg.method as { inPerson?: boolean; virtual?: boolean } | null) || {};
  const format = method.inPerson && method.virtual ? "hybrid" : method.virtual ? "virtual" : "in_person";
  const startStr = String(cfg.start || "");
  const endStr = String(cfg.end || "");
  const date = startStr.split(" · ")[0] || "";
  const t0 = startStr.split(" · ")[1] || "";
  const t1 = endStr.split(" · ")[1] || "";
  const location = (cfg.venueName as string) || (cfg.address as string) || (method.virtual && !method.inPerson ? "Online" : "—");
  // The host id rides the config as `staffId` (the contract's field name); a
  // bare `host` name never implies one.
  const hostId = typeof cfg.staffId === "string" && cfg.staffId.trim() ? cfg.staffId.trim() : null;
  return {
    id,
    title: String(cfg.title || "Untitled event"),
    template: String(cfg.label || ""),
    description: String(cfg.description || ""),
    date,
    time: t1 ? `${t0} — ${t1}` : t0,
    location: String(location),
    host: String(cfg.host || "—"),
    hostId,
    registered: 0,
    checked: 0,
    waitlist: Number(cfg.waitlist || 0),
    capacity: Number(cfg.capacity || 0),
    status: opts.status,
    hue: deriveHue(id),
    heroImage: (cfg.heroImage as string) || null,
    format,
    approvalStatus: "",
    tags: Array.isArray(cfg.tags) ? (cfg.tags as string[]) : [],
    bookingQuestions: Array.isArray(cfg.bookingQuestions) ? (cfg.bookingQuestions as { kind: "question" | "group"; id: string }[]) : [],
    surveys: Array.isArray(cfg.surveys) ? (cfg.surveys as string[]) : [],
    ticketTypeIds: Array.isArray(cfg.ticketTypeIds) ? (cfg.ticketTypeIds as string[]) : [],
    raw: { id, title: String(cfg.title || "") } as unknown as StudioEventDto,
  };
}

/* ── UI status tab → contract `status` query param ───────────────────────
 * "all" sends no status filter. "draft" has no contract status; it filters by
 * visibility client-side after fetch (documented reconciliation) — callers
 * that select Draft should pass view/visibility once the contract models it. */
export function statusParam(tab: UIEventStatus | "all"): StudioEventStatus | "past" | undefined {
  switch (tab) {
    case "live": return "live";
    case "upcoming": return "scheduled";
    case "canceled": return "cancelled";
    case "past": return "past"; // server expands 'past' → completed + archived
    default: return undefined; // all; draft filters client-side until the contract
    // 'draft' status ships (2026-07-12 docket §4) — then the draft tab can pass it.
  }
}

/* ── UI sort column → contract `sort` param (server-backed columns only) ──
 * Server `sort` enum supports: title | start | location | status (+ dir).
 * host / registered / approval have no server sort — those columns are not
 * sortable (documented; never client-fake a server sort). */
export function sortParam(columnId: string): "title" | "start" | "location" | "status" | undefined {
  switch (columnId) {
    case "title": return "title";
    case "when": return "start";
    case "location": return "location";
    case "status": return "status";
    default: return undefined;
  }
}
export const SERVER_SORTABLE = new Set(["title", "when", "location", "status"]);

/** Whether staff check-in is open for an event right now (CC, 2026-10-04), by the rule the events worker
 *  enforces (workers/events/src/routes/studio-admin-guests.ts, both check-in routes): check-in is refused only
 *  BEFORE the event's start date, compared as calendar dates in the event's own timezone (422
 *  CHECK_IN_TOO_EARLY). On and after that date it is allowed, including after the event has ended (late
 *  arrivals, post-event clean-up). A published ("live") event dated in the future is still early. An event
 *  without a readable start never blocks. */
export function checkInWindow(e: Pick<UIEvent, "status" | "raw"> | null | undefined, now: Date = new Date()): "open" | "early" {
  if (!e) return "open";
  const start = e.raw?.startsAt ? new Date(e.raw.startsAt) : null;
  if (!start || Number.isNaN(start.getTime())) return "open";
  const tz = e.raw?.timezone || "UTC";
  const day = (d: Date) => {
    try { return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d); }
    catch { return d.toISOString().slice(0, 10); }
  };
  return day(now) < day(start) ? "early" : "open";
}
